import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import SEOHead from '@/components/marketplace/SEOHead';
import Browse from '@/pages/Browse';

// Resolves the clean canonical taxonomy URL (/category/{type}/{cat}[/{sub}]) to
// its node, then renders the SINGLE reusable <Browse> experience in
// category-scoped mode — so category & subcategory pages inherit the exact same
// filters, sorting, load-more, empty-state and caching as the Type pages.
// Old / renamed paths 301-redirect to the canonical URL. No UUIDs, ever.
const CategoryPage = () => {
  const { listingType } = useParams();
  const params = useParams();
  const path = params['*'] || '';
  const navigate = useNavigate();

  const [node, setNode] = useState(null);
  const [parent, setParent] = useState(null);
  const [level, setLevel] = useState(1);
  const [resolving, setResolving] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setResolving(true);
      setNotFound(false);
      try {
        const { data } = await api.get('/categories/resolve', { params: { type: listingType, path } });
        if (cancelled) return;
        if (data.redirect) {
          navigate(data.redirect, { replace: true });
          return;
        }
        setNode(data.node);
        setParent(data.parent || null);
        setLevel(data.level || 1);
        setResolving(false);
      } catch (e) {
        if (!cancelled) { setNotFound(true); setResolving(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [listingType, path, navigate]);

  if (notFound) {
    return (
      <div className='indus-container py-16 text-center' data-testid='category-not-found'>
        <SEOHead title='Category not found | IndusFact' robots='noindex,follow' />
        Category not found. <Link to='/categories' className='text-primary hover:underline'>Browse all categories</Link>
      </div>
    );
  }

  if (resolving || !node) {
    return (
      <div className='indus-container py-16 text-center text-muted-foreground' data-testid='category-resolving'>
        <SEOHead title='Loading… | IndusFact' robots='noindex,follow' />
        Loading…
      </div>
    );
  }

  return (
    <Browse
      key={node.id}
      listingType={listingType}
      scopedNode={node}
      scopedParent={parent}
      scopedLevel={level}
      scopedPath={path}
    />
  );
};

export default CategoryPage;
